import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ILocationProps } from "../../services/Location/types";
import { MapContainer, TileLayer, Popup, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import DriftMarker from "react-leaflet-drift-marker";
import { IconButton, Tooltip as MuiTooltip } from "@mui/material";

import StopIcon from "@mui/icons-material/Stop";

import vanIcon from "../../assets/images/icons/map/vanIcon.png";
const defaultIcon = L.icon({
    iconUrl: vanIcon,
    iconRetinaUrl: vanIcon,
    iconSize: [40, 41],
    iconAnchor: [12, 41],
    popupAnchor: [8, -34],
    shadowSize: [41, 41],
});
import { t } from "i18next";
import L from "leaflet";
import { useLocations } from "../../pages/LocationPage/_components/LocationContext";

interface MapViewProps {
    selectedId?: string | null;
    sidebarOpen: boolean;
    selectedIds: string[];
}

type MappableLocation = ILocationProps & { latitude: number; longitude: number };

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function createPlateIcon(plate: string): L.DivIcon {
    return L.divIcon({
        className: "vehicle-plate-marker",
        html: `
            <div class="vehicle-plate-marker__container">
                <span class="vehicle-plate-marker__text">${escapeHtml(plate)}</span>
            </div>
        `,
        iconSize: [90, 16],
        iconAnchor: [37, 2],
    });
}

function splitAddressAfterCity(address?: string) {
    const normalized = address?.trim();

    if (!normalized) {
        return { firstLine: "Endereço não encontrado", secondLine: null as string | null };
    }

    const citySeparatorIndex = normalized.indexOf(" - ");
    if (citySeparatorIndex < 0) {
        return { firstLine: normalized, secondLine: null as string | null };
    }

    return {
        firstLine: normalized.slice(0, citySeparatorIndex),
        secondLine: normalized.slice(citySeparatorIndex + 3),
    };
}

function formatPlateDisplay(plate?: string) {
    const normalized = plate?.trim();

    if (!normalized) {
        return "-";
    }

    const upperText = normalized.toUpperCase();
    const plateMatch = upperText.match(/([A-Z]{3})[\s-]?([A-Z0-9]{4})/);

    if (plateMatch) {
        return `${plateMatch[1]}-${plateMatch[2]}`;
    }

    const alphanumeric = upperText.replace(/[^A-Z0-9]/g, "");

    if (!alphanumeric) {
        return normalized;
    }

    const base = alphanumeric.slice(0, 7);
    return base.length <= 3 ? base : `${base.slice(0, 3)}-${base.slice(3)}`;
}

function hasCoordinates(location: ILocationProps): location is MappableLocation {
    return (
        location.hasLocation === true
        && typeof location.latitude === "number"
        && Number.isFinite(location.latitude)
        && typeof location.longitude === "number"
        && Number.isFinite(location.longitude)
    );
}

function MapController({
    selectedLocation,
    isFollowing,
}: {
    selectedLocation?: MappableLocation;
    isFollowing: boolean;
}) {
    const map = useMap();

    useEffect(() => {
        if (isFollowing && selectedLocation) {
            map.flyTo([selectedLocation.latitude, selectedLocation.longitude], 17, {
                animate: true,
                duration: 1,
            });
        }
    }, [isFollowing, map, selectedLocation]);

    return null;
}

function ResizeController({ sidebarOpen }: { sidebarOpen: boolean }) {
    const map = useMap();

    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
            map.setView(map.getCenter(), map.getZoom(), {
                animate: true,
                duration: 0.5,
            });
        }, 1);

        return () => clearTimeout(timer);
    }, [sidebarOpen, map]);

    return null;
}

function InitialBoundsController({ locations }: { locations: ILocationProps[] }) {
    const map = useMap();
    const hasFitted = useRef(false);

    useEffect(() => {
        if (!hasFitted.current && locations.length > 0) {
            const bounds = L.latLngBounds(
                locations
                    .filter(hasCoordinates)
                    .map(loc => [loc.latitude, loc.longitude])
            );
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [50, 50] });
                hasFitted.current = true;
            }
        }
    }, [locations, map]);

    return null;
}

export default function MapView({ selectedId, sidebarOpen = true, selectedIds }: MapViewProps) {
    const locations = useLocations();
    const [isFollowing, setIsFollowing] = useState(true);

    useEffect(() => {
        if (selectedId) {
            setIsFollowing(true);
        }
    }, [selectedId]);

    const markersToRender = useMemo<MappableLocation[]>(() => {
        return locations
            .filter(l => selectedIds.length === 0 || selectedIds.includes(l.entityId))
            .filter(hasCoordinates);
    }, [locations, selectedIds]);

    const selectedLocation = useMemo(() => {
        if (!selectedId) {
            return undefined;
        }

        const found = locations.find((location) => location.entityId === selectedId);
        return found && hasCoordinates(found) ? found : undefined;
    }, [locations, selectedId]);

    return (
        <MapContainer center={[0, 0]} zoom={2} className="map-container" style={{ height: "100%", width: "100%" }}>
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {markersToRender.map((loc) => {
                const plateLabel = formatPlateDisplay(loc.vehiclePlate || loc.entityId);
                const addressLines = splitAddressAfterCity(loc.address);
                const isOffline = loc.isOnline === false;

                return (
                    <Fragment key={loc.entityId}>
                        <DriftMarker
                            key={`vehicle-${loc.entityId}`}
                            position={[loc.latitude, loc.longitude]}
                            duration={1000}
                            icon={defaultIcon}
                        >
                            <Popup autoClose={false} closeButton={true} autoPan={false} keepInView={false}>
                                <div style={{
                                    fontFamily: "Inter, Roboto, sans-serif",
                                    fontSize: "11px",
                                    lineHeight: 1.3,
                                    color: "#333",
                                    padding: "2px 4px",
                                }}>
                                    <div style={{ fontWeight: 600 }}>{plateLabel}</div>
                                    {!!loc.modelName && <div style={{ color: "#777" }}>{loc.modelName}</div>}
                                    <div style={{ color: "#777" }}>
                                        {addressLines.firstLine}
                                        {addressLines.secondLine && <><br />{addressLines.secondLine}</>}
                                    </div>
                                    <div style={{ color: "#777" }}>
                                        Lat/Lng: {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                                    </div>
                                </div>
                            </Popup>
                        </DriftMarker>

                        <Marker
                            key={`plate-${loc.entityId}`}
                            position={[loc.latitude, loc.longitude]}
                            icon={createPlateIcon(plateLabel)}
                            interactive={false}
                            keyboard={false}
                            zIndexOffset={-1000}
                        />
                    </Fragment>
                );
            })}

            <InitialBoundsController locations={markersToRender} />

            <MapController selectedLocation={selectedLocation} isFollowing={isFollowing} />
            <ResizeController sidebarOpen={sidebarOpen} />

            {selectedId && (
                <>
                    {isFollowing && (
                        <MuiTooltip title={t("map.stopFollow")}>
                            <IconButton
                                onClick={() => setIsFollowing(false)}
                                sx={{
                                    position: "absolute",
                                    top: 135,
                                    left: 5,
                                    zIndex: 1000,
                                    backgroundColor: "white",
                                    boxShadow: 2,
                                    borderRadius: "50%",
                                    width: 48,
                                    height: 48,
                                    "&:hover": { backgroundColor: "white", opacity: 1 },
                                }}
                            >
                                <StopIcon />
                            </IconButton>
                        </MuiTooltip>
                    )}
                </>
            )}
        </MapContainer>
    );
}
